(() => {
  'use strict';

  if (customElements.get('veroshape-size-finder')) return;

  const SIZES = ['S', 'M', 'L', 'XL', '2XL'];

  const BANDS = [
    { upper: 50, size: 'S' },
    { upper: 78.5, size: 'M' },
    { upper: 106.5, size: 'L' },
    { upper: 133.5, size: 'XL' },
    { upper: Infinity, size: '2XL' }
  ];

  class VeroShapeSizeFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.dialog = this.querySelector('[data-dialog]');
      this.form = this.querySelector('[data-form]');
      this.steps = [...this.querySelectorAll('[data-step]')];
      this.currentStep = 0;
      this.defaultUnits = this.detectUnits();
      this.units = null;
      this.result = null;
      this.lastFocus = null;
      this.touchedSteps = new Set();
      this.analysisTimers = [];
      this.unitConfirmed = false;
      this.unitPromptTimer = null;

      this.bind();
      this.setUnits(this.defaultUnits);
      this.updateWheel(this.querySelector('[data-wheel="age"]'));
      this.showStep(0);
    }

    bind() {
      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-vs-size-open]');

        if (opener) {
          event.preventDefault();
          this.open(opener);
        }
      });

      this.querySelector('[data-close]').addEventListener(
        'click',
        () => this.close()
      );

      this.querySelector('[data-next]').addEventListener(
        'click',
        () => this.next()
      );

      this.querySelector('[data-previous]').addEventListener(
        'click',
        () => this.previous()
      );

      this.querySelector('[data-header-restart]').addEventListener(
        'click',
        () => this.restart()
      );

      this.querySelector('[data-select-size]').addEventListener(
        'click',
        () => this.selectRecommendedSize()
      );

      this.querySelectorAll('[data-unit]').forEach((button) => {
        button.addEventListener(
          'click',
          () => this.setUnits(button.dataset.unit, true)
        );
      });

      this.querySelectorAll('[data-value-input]').forEach((input) => {
        input.addEventListener('input', () => {
          this.touchedSteps.add(this.currentStep);
          this.updateWheel(input.closest('[data-wheel]'));
          this.updateNextState();
        });
      });

      this.bindWheelGestures();

      this.querySelectorAll('[data-adjust]').forEach((button) => {
        button.addEventListener('click', () => {
          const wheel = button.closest('[data-wheel]');
          const input = wheel.querySelector('[data-value-input]');
          const direction = Number(button.dataset.adjust);
          const step = Number(input.step) || 1;
          const minimum = Number(input.min);
          const maximum = Number(input.max);

          const value = Math.min(
            maximum,
            Math.max(
              minimum,
              Number(input.value) + direction * step
            )
          );

          input.value = String(value);
          input.dispatchEvent(
            new Event('input', { bubbles: true })
          );
        });
      });

      this.form.addEventListener('change', (event) => {
        if (event.target.matches('input[type="radio"]')) {
          this.querySelector('[data-error]').hidden = true;
          window.setTimeout(() => this.next(), 180);
        }
      });

      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) {
          this.close();
        }
      });

      this.dialog.addEventListener('close', () => {
        document.body.classList.remove('vs-size-is-open');

        if (this.lastFocus) {
          this.lastFocus.focus();
        }
      });
    }

    detectUnits() {
      const country = String(
        window.Shopify && window.Shopify.country
          ? window.Shopify.country
          : ''
      ).toUpperCase();

      return country === '' || country === 'US'
        ? 'imperial'
        : 'metric';
    }

    open(opener) {
      this.lastFocus = opener;
      document.body.classList.add('vs-size-is-open');

      if (!this.dialog.open) {
        this.dialog.showModal();
      }

      const first =
        this.steps[this.currentStep] &&
        this.steps[this.currentStep].querySelector(
          'input,button'
        );

      if (first) {
        window.setTimeout(() => first.focus(), 50);
      }

      this.promptUnits();
      this.track('size_finder_opened');
    }

    close() {
      if (this.dialog.open) {
        this.dialog.close();
      }
    }

    setUnits(units, confirmed = false) {
      if (!['metric', 'imperial'].includes(units)) return;

      if (confirmed) {
        this.unitConfirmed = true;
        window.clearTimeout(this.unitPromptTimer);

        this.querySelectorAll(
          '.vs-size__unit-switch'
        ).forEach((switcher) => {
          switcher.classList.remove('is-prompting');
        });
      }

      const previousUnits = this.units;
      const heightInput = this.form.elements.height_value;
      const weightInput = this.form.elements.weight_value;

      let heightValue = Number(heightInput.value);
      let weightValue = Number(weightInput.value);

      if (previousUnits && previousUnits !== units) {
        if (units === 'metric') {
          heightValue = Math.round(heightValue * 2.54);
          weightValue =
            Math.round(
              weightValue * 0.45359237 * 2
            ) / 2;
        } else {
          heightValue = Math.round(heightValue / 2.54);
          weightValue = Math.round(
            weightValue / 0.45359237
          );
        }
      } else if (!previousUnits && units === 'imperial') {
        heightValue = 69;
        weightValue = 172;
      }

      this.units = units;

      if (units === 'metric') {
        heightInput.min = '140';
        heightInput.max = '210';
        heightInput.step = '1';

        weightInput.min = '40';
        weightInput.max = '180';
        weightInput.step = '0.5';
      } else {
        heightInput.min = '55';
        heightInput.max = '83';
        heightInput.step = '1';

        weightInput.min = '88';
        weightInput.max = '397';
        weightInput.step = '1';
      }

      heightInput.value = String(
        Math.min(
          Number(heightInput.max),
          Math.max(
            Number(heightInput.min),
            heightValue
          )
        )
      );

      weightInput.value = String(
        Math.min(
          Number(weightInput.max),
          Math.max(
            Number(weightInput.min),
            weightValue
          )
        )
      );

      this.querySelectorAll('[data-unit]').forEach((button) => {
        const active = button.dataset.unit === units;

        button.classList.toggle('is-active', active);
        button.setAttribute(
          'aria-pressed',
          String(active)
        );
      });

      this.updateWheel(
        this.querySelector('[data-wheel="height"]')
      );

      this.updateWheel(
        this.querySelector('[data-wheel="weight"]')
      );

      this.querySelector('[data-error]').hidden = true;
      this.updateNextState();
    }

    promptUnits() {
      if (this.unitConfirmed || !this.dialog.open) return;

      const step = this.steps[this.currentStep];
      const switcher =
        step &&
        step.querySelector('.vs-size__unit-switch');

      if (!switcher) return;

      window.clearTimeout(this.unitPromptTimer);
      switcher.classList.remove('is-prompting');

      void switcher.offsetWidth;

      switcher.classList.add('is-prompting');

      this.unitPromptTimer = window.setTimeout(() => {
        switcher.classList.remove('is-prompting');
      }, 2200);
    }

    updateWheel(wheel) {
      if (!wheel) return;

      const input =
        wheel.querySelector('[data-value-input]');

      const output =
        wheel.querySelector('[data-value-output]');

      const value = Number(input.value);
      const type = wheel.dataset.wheel;

      if (type === 'height') {
        if (this.units === 'imperial') {
          const feet = Math.floor(value / 12);
          const inches = Math.round(value % 12);

          output.textContent =
            `${feet} ${this.dataset.unitFt} ` +
            `${inches} ${this.dataset.unitIn}`;
        } else {
          output.textContent =
            `${Math.round(value)} ${this.dataset.unitCm}`;
        }
      }

      if (type === 'weight') {
        const displayed =
          this.units === 'metric'
            ? Number(value.toFixed(1))
            : Math.round(value);

        const unit =
          this.units === 'metric'
            ? this.dataset.unitKg
            : this.dataset.unitLb;

        output.textContent = `${displayed} ${unit}`;
      }

      if (type === 'age') {
        output.textContent =
          `${Math.round(value)} ${this.dataset.unitYears}`;
      }
    }

    values() {
      const elements = this.form.elements;

      const heightCm =
        this.units === 'metric'
          ? Number(elements.height_value.value)
          : Number(elements.height_value.value) * 2.54;

      const weightKg =
        this.units === 'metric'
          ? Number(elements.weight_value.value)
          : Number(elements.weight_value.value) *
            0.45359237;

      return {
        heightCm,
        weightKg,
        age: Number(elements.age_value.value),
        chest: elements.chest.value,
        stomach: elements.stomach.value,
        rideUp: elements.ride_up.value,
        compression: elements.compression.value
      };
    }

    valid() {
      const value = this.values();

      if (this.currentStep === 0) {
        return (
          this.touchedSteps.has(0) &&
          value.heightCm >= 140 &&
          value.heightCm <= 211
        );
      }

      if (this.currentStep === 1) {
        return (
          this.touchedSteps.has(1) &&
          value.weightKg >= 40 &&
          value.weightKg <= 181
        );
      }

      if (this.currentStep === 2) {
        return (
          this.touchedSteps.has(2) &&
          value.age >= 18 &&
          value.age <= 85
        );
      }

      if (this.currentStep === 3) {
        return Boolean(value.chest);
      }

      if (this.currentStep === 4) {
        return Boolean(value.stomach);
      }

      if (this.currentStep === 5) {
        return Boolean(value.rideUp);
      }

      return Boolean(value.compression);
    }

    showStep(index) {
      this.currentStep = index;

      this.steps.forEach((step, stepIndex) => {
        step.hidden = stepIndex !== index;
      });

      this.querySelector('[data-analysis]').hidden = true;
      this.querySelector('[data-result]').hidden = true;
      this.querySelector('[data-error]').hidden = true;
      this.querySelector('[data-progress]').hidden = false;
      this.querySelector('[data-previous]').hidden =
        index === 0;
      this.querySelector(
        '[data-header-restart]'
      ).hidden = true;
      this.querySelector(
        '[data-select-size]'
      ).hidden = true;

      this.querySelectorAll('[data-progress] i').forEach(
        (dot, dotIndex) => {
          dot.classList.toggle(
            'is-active',
            dotIndex === index
          );

          dot.classList.toggle(
            'is-complete',
            dotIndex < index
          );
        }
      );

      this.updateNextState();

      const focusTarget =
        this.steps[index].querySelector(
          '[data-value-input], input[type="radio"]'
        );

      if (this.dialog.open && focusTarget) {
        window.setTimeout(
          () =>
            focusTarget.focus({
              preventScroll: true
            }),
          60
        );
      }

      this.promptUnits();
    }

    updateNextState() {
      const next = this.querySelector('[data-next]');
      const step = this.steps[this.currentStep];

      if (!step || step.dataset.kind !== 'numeric') {
        next.hidden = true;
        return;
      }

      next.hidden = false;
      next.disabled = !this.valid();

      if (this.touchedSteps.has(this.currentStep)) {
        next.textContent = this.dataset.nextText;
        return;
      }

      const prompts = [
        this.dataset.selectHeightText,
        this.dataset.selectWeightText,
        this.dataset.selectAgeText
      ];

      next.textContent =
        prompts[this.currentStep] ||
        this.dataset.nextText;
    }

    next() {
      if (!this.valid()) {
        this.querySelector('[data-error]').hidden = false;
        return;
      }

      if (this.currentStep < this.steps.length - 1) {
        this.showStep(this.currentStep + 1);
        return;
      }

      this.result = this.calculate();
      this.showAnalysis();
    }

    previous() {
      if (this.currentStep > 0) {
        this.showStep(this.currentStep - 1);
      }
    }

    calculate() {
      const value = this.values();

      const chestAdjustment = {
        narrower: -1,
        average: 0,
        broader: 2
      }[value.chest];

      const stomachAdjustment = {
        flatter: -1,
        average: 0,
        rounder: 2
      }[value.stomach];

      const score =
        value.weightKg * 175 / value.heightCm +
        chestAdjustment +
        stomachAdjustment +
        (value.age >= 39 ? 0.5 : 0);

      const baseRecommendedSize =
        BANDS.find(
          (band) => score < band.upper
        ).size;

      const boundaries = BANDS
        .map((band) => band.upper)
        .filter(Number.isFinite);

      const nearest = boundaries
        .map((boundary) => ({
          boundary,
          distance: Math.abs(score - boundary)
        }))
        .sort(
          (a, b) => a.distance - b.distance
        )[0];

      const baseIndex =
        SIZES.indexOf(baseRecommendedSize);

      const baseAlternative =
        score < nearest.boundary
          ? SIZES[baseIndex + 1]
          : SIZES[baseIndex - 1];

      let recommendedSize = baseRecommendedSize;
      let preferenceAdjusted = false;

      if (
        value.compression === 'extra_firm' &&
        baseIndex > 0
      ) {
        const lowerBoundary =
          BANDS[baseIndex - 1].upper;

        const upperBoundary =
          BANDS[baseIndex].upper;

        const previousBoundary =
          baseIndex > 1
            ? BANDS[baseIndex - 2].upper
            : lowerBoundary -
              (upperBoundary - lowerBoundary);

        const bandWidth =
          Number.isFinite(upperBoundary)
            ? upperBoundary - lowerBoundary
            : lowerBoundary - previousBoundary;

        const extraFirmLimit =
          lowerBoundary + bandWidth * 0.35;

        if (score <= extraFirmLimit) {
          recommendedSize = SIZES[baseIndex - 1];
          preferenceAdjusted = true;
        }
      }

      const alternativeSize =
        value.compression === 'extra_firm'
          ? preferenceAdjusted
            ? baseRecommendedSize
            : null
          : nearest.distance <= 1.5
            ? baseAlternative
            : null;

      let lengthRisk =
        value.heightCm >= 194
          ? 'high'
          : value.heightCm >= 187
            ? 'caution'
            : 'low';

      if (value.rideUp === 'yes') {
        lengthRisk =
          lengthRisk === 'low'
            ? 'caution'
            : 'high';
      }

      return {
        recommendedSize,
        baseRecommendedSize,
        alternativeSize,
        preferenceAdjusted,
        lengthRisk,
        score: Math.round(score * 100) / 100,
        ...value
      };
    }

    showAnalysis() {
      this.clearAnalysisTimers();

      this.steps.forEach((step) => {
        step.hidden = true;
      });

      this.querySelector('[data-analysis]').hidden = false;
      this.querySelector('[data-result]').hidden = true;
      this.querySelector('[data-progress]').hidden = true;
      this.querySelector('[data-next]').hidden = true;
      this.querySelector('[data-previous]').hidden = true;
      this.querySelector(
        '[data-header-restart]'
      ).hidden = true;
      this.querySelector(
        '[data-select-size]'
      ).hidden = true;

      this.setAnalysisStage(0);

      this.analysisTimers.push(
        window.setTimeout(
          () => this.setAnalysisStage(1),
          1500
        )
      );

      this.analysisTimers.push(
        window.setTimeout(
          () => this.setAnalysisStage(2),
          3000
        )
      );

      this.analysisTimers.push(
        window.setTimeout(
          () => this.renderResult(),
          4500
        )
      );
    }

    setAnalysisStage(index) {
      const message = this.querySelector(
        `[data-analysis-message-${index}]`
      );

      this.querySelector(
        '[data-analysis-text]'
      ).textContent = message.textContent;

      this.querySelector(
        '[data-analysis-bar]'
      ).style.width = `${[30, 64, 94][index]}%`;

      this.querySelectorAll(
        '.vs-size__analysis-stages li'
      ).forEach((item, itemIndex) => {
        item.classList.toggle(
          'is-active',
          itemIndex === index
        );

        item.classList.toggle(
          'is-complete',
          itemIndex < index
        );
      });
    }

    bindWheelGestures() {
      this.querySelectorAll('[data-wheel]').forEach(
        (wheel) => {
          const ruler =
            wheel.querySelector('.vs-size__ruler');

          const input =
            wheel.querySelector('[data-value-input]');

          const vertical =
            wheel.classList.contains(
              'vs-size__wheel--vertical'
            );

          let pointerId = null;
          let startPosition = 0;
          let startValue = 0;

          const position = (event) =>
            vertical
              ? event.clientY
              : event.clientX;

          const finish = (event) => {
            if (
              pointerId === null ||
              event.pointerId !== pointerId
            ) {
              return;
            }

            if (ruler.hasPointerCapture(pointerId)) {
              ruler.releasePointerCapture(pointerId);
            }

            pointerId = null;
            ruler.classList.remove('is-dragging');
          };

          ruler.addEventListener(
            'pointerdown',
            (event) => {
              if (!event.isPrimary) return;

              pointerId = event.pointerId;
              startPosition = position(event);
              startValue = Number(input.value);

              ruler.setPointerCapture(pointerId);
              ruler.classList.add('is-dragging');

              input.focus({
                preventScroll: true
              });

              event.preventDefault();
            }
          );

          ruler.addEventListener(
            'pointermove',
            (event) => {
              if (
                pointerId === null ||
                event.pointerId !== pointerId
              ) {
                return;
              }

              const distance =
                vertical
                  ? startPosition - position(event)
                  : position(event) - startPosition;

              const pixelsPerStep =
                vertical ? 3.5 : 3;

              const step =
                Number(input.step) || 1;

              const steps =
                Math.round(
                  distance / pixelsPerStep
                );

              this.setWheelValue(
                input,
                startValue + steps * step
              );

              event.preventDefault();
            }
          );

          ruler.addEventListener(
            'pointerup',
            finish
          );

          ruler.addEventListener(
            'pointercancel',
            finish
          );

          ruler.addEventListener(
            'wheel',
            (event) => {
              const movement =
                Math.abs(event.deltaY) >=
                Math.abs(event.deltaX)
                  ? event.deltaY
                  : event.deltaX;

              if (movement === 0) return;

              const step =
                Number(input.step) || 1;

              this.setWheelValue(
                input,
                Number(input.value) -
                  Math.sign(movement) * step
              );

              event.preventDefault();
            },
            { passive: false }
          );
        }
      );
    }

    setWheelValue(input, value) {
      const minimum = Number(input.min);
      const maximum = Number(input.max);
      const step = Number(input.step) || 1;

      const precision =
        String(step).includes('.')
          ? String(step).split('.')[1].length
          : 0;

      const limited = Math.min(
        maximum,
        Math.max(minimum, value)
      );

      const normalized = Number(
        (
          Math.round(limited / step) * step
        ).toFixed(precision)
      );

      if (Number(input.value) === normalized) return;

      input.value = String(normalized);

      input.dispatchEvent(
        new Event('input', { bubbles: true })
      );
    }

    clearAnalysisTimers() {
      this.analysisTimers.forEach((timer) =>
        window.clearTimeout(timer)
      );

      this.analysisTimers = [];
    }

    renderResult() {
      const result = this.result;

      this.querySelector('[data-analysis]').hidden = true;
      this.querySelector('[data-result]').hidden = false;
      this.querySelector('[data-progress]').hidden = true;
      this.querySelector('[data-previous]').hidden = true;
      this.querySelector(
        '[data-header-restart]'
      ).hidden = false;

      this.querySelector(
        '[data-result-size]'
      ).textContent = result.recommendedSize;

      const resultCopy =
        this.querySelector('[data-result-copy]');

      if (result.compression === 'extra_firm') {
        resultCopy.textContent =
          result.preferenceAdjusted
            ? this.dataset.extraFirmAdjusted
            : this.dataset.extraFirmSame;
      } else {
        resultCopy.textContent =
          this.dataset.balancedFit;
      }

      this.querySelector(
        '[data-between]'
      ).hidden = !result.alternativeSize;

      this.querySelector(
        '[data-alternative-size]'
      ).textContent =
        result.alternativeSize || '';

      this.querySelector(
        '[data-length-caution]'
      ).hidden =
        result.lengthRisk !== 'caution';

      this.querySelector(
        '[data-length-high]'
      ).hidden =
        result.lengthRisk !== 'high';

      this.querySelector(
        '[data-select-size]'
      ).hidden = false;

      this.track('size_recommended', {
        recommended_size: result.recommendedSize,
        base_recommended_size:
          result.baseRecommendedSize,
        alternative_size:
          result.alternativeSize || '',
        length_risk: result.lengthRisk,
        compression_preference:
          result.compression,
        preference_adjusted:
          result.preferenceAdjusted
      });

      this.dispatchEvent(
        new CustomEvent(
          'veroshape:size-recommendation',
          {
            bubbles: true,
            detail: {
              recommendedSize:
                result.recommendedSize,
              baseRecommendedSize:
                result.baseRecommendedSize,
              alternativeSize:
                result.alternativeSize,
              lengthRisk:
                result.lengthRisk,
              compressionPreference:
                result.compression,
              preferenceAdjusted:
                result.preferenceAdjusted,
              engineVersion:
                this.dataset.engineVersion
            }
          }
        )
      );
    }

    normalizeSize(value) {
      const compact = String(value || '')
        .toUpperCase()
        .replace(
          /SOLD OUT|UNAVAILABLE|ÉPUISÉ|INDISPONIBLE/g,
          ''
        )
        .replace(/[^A-Z0-9]/g, '');

      const aliases = {
        SMALL: 'S',
        MEDIUM: 'M',
        LARGE: 'L',
        XLARGE: 'XL',
        EXTRALARGE: 'XL',
        XXL: '2XL',
        XXLARGE: '2XL',
        '2XLARGE': '2XL',
        '2EXTRALARGE': '2XL'
      };

      return aliases[compact] || compact;
    }

    isSizeName(value) {
      return /^(size|taille)$/i.test(
        String(value || '')
          .replace(/[:*]/g, '')
          .trim()
      );
    }

    isVisible(element) {
      return Boolean(
        element &&
        element.getClientRects().length &&
        window.getComputedStyle(element).visibility !==
          'hidden'
      );
    }

    productRoots() {
      const roots = [];

      const add = (root) => {
        if (root && !roots.includes(root)) {
          roots.push(root);
        }
      };

      if (this.lastFocus) {
        add(
          this.lastFocus.closest('product-info')
        );

        add(
          this.lastFocus.closest(
            '.product__info-container'
          )
        );

        add(
          this.lastFocus.closest('.product')
        );

        add(
          this.lastFocus.closest('[data-section]')
        );
      }

      add(document);

      return roots;
    }

    findGloboSizeControl(root, normalized) {
      const legends = [
        ...root.querySelectorAll(
          'legend.name-option[data-name], ' +
          '.name-option[data-name]'
        )
      ].filter((legend) => {
        return this.isSizeName(
          legend.dataset.name ||
          legend.textContent
        );
      });

      for (const legend of legends) {
        const container = legend.parentElement;

        if (!container) continue;

        const inputs = [
          ...container.querySelectorAll(
            'input[type="radio"], ' +
            'input[type="checkbox"]'
          )
        ];

        const input = inputs.find((item) => {
          return (
            !item.disabled &&
            this.normalizeSize(item.value) ===
              normalized
          );
        });

        if (input) {
          const label = input.id
            ? container.querySelector(
                `label[for="${CSS.escape(
                  input.id
                )}"]`
              )
            : input.closest('label');

          return {
            type: 'click',
            control: label || input,
            input
          };
        }

        const selects = [
          ...container.querySelectorAll('select')
        ];

        for (const select of selects) {
          const option = [
            ...select.options
          ].find((item) => {
            return (
              this.normalizeSize(
                item.value ||
                item.textContent
              ) === normalized
            );
          });

          if (option) {
            return {
              type: 'select',
              control: select,
              value: option.value
            };
          }
        }

        const clickables = [
          ...container.querySelectorAll(
            'label, button, [data-value], ' +
            '[data-option-value], [role="radio"]'
          )
        ];

        const clickable = clickables.find((item) => {
          if (!this.isVisible(item)) return false;

          const value =
            item.dataset.value ||
            item.dataset.optionValue ||
            item.getAttribute('title') ||
            item.textContent;

          return (
            this.normalizeSize(value) === normalized
          );
        });

        if (clickable) {
          return {
            type: 'click',
            control: clickable
          };
        }
      }

      return null;
    }

    findNativeSizeControl(root, normalized) {
      const pickers = [
        ...root.querySelectorAll('variant-selects')
      ];

      for (const picker of pickers) {
        for (
          const select of picker.querySelectorAll(
            'select[name^="options["]'
          )
        ) {
          const optionName =
            (
              select.name.match(
                /options\[(.+)\]/
              ) || []
            )[1] || '';

          if (!this.isSizeName(optionName)) {
            continue;
          }

          const option = [
            ...select.options
          ].find((item) => {
            return (
              this.normalizeSize(
                item.value ||
                item.textContent
              ) === normalized
            );
          });

          if (option) {
            return {
              type: 'select',
              control: select,
              value: option.value
            };
          }
        }

        const fieldsets = [
          ...picker.querySelectorAll('fieldset')
        ];

        for (const fieldset of fieldsets) {
          const legend =
            fieldset.querySelector('legend');

          if (
            !legend ||
            !this.isSizeName(legend.textContent)
          ) {
            continue;
          }

          const radio = [
            ...fieldset.querySelectorAll(
              'input[type="radio"]'
            )
          ].find((item) => {
            return (
              !item.disabled &&
              this.normalizeSize(item.value) ===
                normalized
            );
          });

          if (!radio) continue;

          const label = radio.id
            ? fieldset.querySelector(
                `label[for="${CSS.escape(
                  radio.id
                )}"]`
              )
            : radio.closest('label');

          return {
            type: 'click',
            control: label || radio,
            input: radio
          };
        }
      }

      return null;
    }

    findSizeControl(size) {
      const normalized =
        this.normalizeSize(size);

      for (const root of this.productRoots()) {
        const globoControl =
          this.findGloboSizeControl(
            root,
            normalized
          );

        if (globoControl) {
          return globoControl;
        }
      }

      for (const root of this.productRoots()) {
        const nativeControl =
          this.findNativeSizeControl(
            root,
            normalized
          );

        if (nativeControl) {
          return nativeControl;
        }
      }

      return null;
    }

    activateSizeControl(target) {
      if (target.type === 'select') {
        target.control.value = target.value;

        target.control.dispatchEvent(
          new Event('input', {
            bubbles: true
          })
        );

        target.control.dispatchEvent(
          new Event('change', {
            bubbles: true
          })
        );

        return;
      }

      target.control.click();

      if (
        target.input &&
        !target.input.checked
      ) {
        target.input.checked = true;

        target.input.dispatchEvent(
          new Event('input', {
            bubbles: true
          })
        );

        target.input.dispatchEvent(
          new Event('change', {
            bubbles: true
          })
        );
      }
    }

    selectRecommendedSize() {
      if (!this.result) return;

      const size = this.result.recommendedSize;
      const target = this.findSizeControl(size);

      if (!target) {
        const error =
          this.querySelector('[data-error]');

        error.textContent =
          this.dataset.errorText;

        error.hidden = false;

        this.track(
          'recommended_size_selection_failed',
          { size }
        );

        return;
      }

      this.activateSizeControl(target);

      this.track(
        'recommended_size_selected',
        { size }
      );

      window.setTimeout(
        () => this.close(),
        180
      );
    }

    restart() {
      this.clearAnalysisTimers();
      this.form.reset();
      this.touchedSteps.clear();
      this.unitConfirmed = false;
      this.units = null;

      this.setUnits(this.defaultUnits);

      this.updateWheel(
        this.querySelector('[data-wheel="age"]')
      );

      this.result = null;
      this.showStep(0);
    }

    track(eventName, detail = {}) {
      window.dataLayer =
        window.dataLayer || [];

      window.dataLayer.push({
        event: `veroshape_${eventName}`,
        engine_version:
          this.dataset.engineVersion,
        ...detail
      });
    }
  }

  customElements.define(
    'veroshape-size-finder',
    VeroShapeSizeFinder
  );
})();